import React from "react";
import {
  DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowRightLeft, GripVertical, X } from "lucide-react";

function SortableTile({ id, onRemove, onMoveTo, children, removeLabel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} data-testid={`nds-arrange-item-${id}`}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 30 : undefined }}
      className={`relative rounded-xl touch-none ${isDragging ? "scale-[1.03] shadow-2xl shadow-brand-cyan/20 opacity-90" : "nds-arrange-wiggle"}`}>
      <div className="pointer-events-none select-none h-full grid [&>*]:w-full">{children}</div>
      {/* Whole card is the drag handle while arranging — clicks can't open it by accident. */}
      <div {...attributes} {...listeners} data-testid={`nds-arrange-handle-${id}`} aria-label="Drag to move card"
        className="absolute inset-0 rounded-xl cursor-grab active:cursor-grabbing ring-1 ring-dashed ring-brand-cyan/40 hover:ring-brand-cyan/80 transition-[box-shadow]" />
      <div className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-none w-6 h-6 grid place-items-center rounded-md bg-navy-950/80 border border-brand-cyan/40 text-brand-cyan">
        <GripVertical className="w-3.5 h-3.5" />
      </div>
      {onRemove && removeLabel && <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onRemove(id)} data-testid={`nds-arrange-remove-${id}`} aria-label={`${removeLabel} card`}
        className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-2 h-7 rounded-full bg-rose-500 text-white font-mono text-[0.55rem] tracking-[0.14em] uppercase shadow-lg hover:bg-rose-400 active:scale-95 transition-[background-color,transform]">
        <X className="w-3 h-3" strokeWidth={2.5} /> {removeLabel}
      </button>}
      {onRemove && !removeLabel && <button type="button" onClick={() => onRemove(id)} data-testid={`nds-arrange-remove-${id}`} aria-label="Remove card from board"
        className="absolute -top-2 -right-2 w-7 h-7 grid place-items-center rounded-full bg-rose-500 text-white shadow-lg hover:bg-rose-400 active:scale-90 transition-[background-color,transform]">
        <X className="w-4 h-4" strokeWidth={2.5} />
      </button>}
      {onMoveTo && (
        <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => onMoveTo(id)}
          data-testid={`nds-arrange-move-to-${id}`} aria-label="Move card to another engine"
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-navy-950/90 border border-brand-cyan/60 text-brand-cyan font-mono text-[0.55rem] tracking-[0.14em] uppercase hover:bg-brand-cyan/20 transition-colors">
          <ArrowRightLeft className="w-3 h-3" /> Move to…
        </button>
      )}
    </div>
  );
}

// Snap-to-grid arrange mode for the capability board (drag to reorder, X to remove).
export default function ArrangeGrid({ ids, renderTile, onMove, onRemove, onMoveTo, removeLabel, gridClass = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => { if (over && active.id !== over.id) onMove(active.id, over.id); }}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className={`grid ${gridClass} gap-3`} data-testid="nds-cap-grid-arrange">
          {ids.map((id) => <SortableTile key={id} id={id} onRemove={onRemove} onMoveTo={onMoveTo} removeLabel={removeLabel}>{renderTile(id)}</SortableTile>)}
        </div>
      </SortableContext>
      <style>{`
        @keyframes ndsWiggle { 0%,100% { rotate: -0.4deg } 50% { rotate: 0.4deg } }
        .nds-arrange-wiggle { animation: ndsWiggle 0.5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .nds-arrange-wiggle { animation: none; } }
      `}</style>
    </DndContext>
  );
}
